import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Pagination, Tag } from '@carbon/react';
import { Locked, Logout, TrashCan } from '@carbon/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { DataTable } from '../components/DataTable';
import { InlineError, PageHeader } from '../components/states';
import { CURRENCY, Money, When } from '../components/format';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';
import { useAuth } from '../auth/AuthContext';
import { BulkLockDialog, GameLockDialog, RiskTags } from './Player360';
import { useCanDeleteId, XoaIdDialog } from '../components/XoaId';

export type Player = {
  id: number; user_name: string; nick_name: string | null; vin: number; vin_total: number; xu: number;
  vip_point: number; created_at: string; khoa: number; ref_code: string | null; la_test?: number; online?: boolean | null;
  marks?: { watchlist: boolean; suspectedBot: boolean; suspectedMulti: boolean } | null;
  flags?: { withdrawFrozen: boolean; chatMutedUntil: number | null; lockedGames: string[] } | null;
  agent?: { id: string; code: string; displayName: string } | null;
};
type UsersPage = { items: Player[]; total: number; page: number; pageSize: number };

export function PlayerTags({ p, online }: { p: Player; online: boolean | null | undefined }) {
  return <>
    {Number(p.khoa) === 1 ? <Tag type="red" size="sm">Đã khóa</Tag> : null}
    {Number(p.la_test) === 1 ? <Tag type="purple" size="sm">Tài khoản thử</Tag> : null}
    {online ? <Tag type="green" size="sm">Đang online</Tag> : null}
    {p.flags?.withdrawFrozen ? <Tag type="red" size="sm">Dừng rút</Tag> : null}
    {p.flags?.chatMutedUntil ? <Tag type="warm-gray" size="sm">Cấm chat</Tag> : null}
    {p.flags?.lockedGames?.length ? <Tag type="cool-gray" size="sm">Khóa {p.flags.lockedGames.length} game</Tag> : null}
    <RiskTags marks={p.marks} />
  </>;
}

type RowAct = { kind: 'kick' | 'games' | 'xoa'; p: Player };

// Kick and per-game bet lock sit next to the player's ID (client 2026-10-01): the first column, so they stay
// on screen in the phone list mode too. A click there never opens the player page.
function columnsFor(onAct: (a: RowAct) => void, canKick: boolean, canLock: boolean, canDelete: boolean): ColumnDef<Player, any>[] {
  return [
    { header: 'Mã', accessorKey: 'id', cell: (c) => {
      const p = c.row.original;
      const act = (kind: RowAct['kind']) => (e: { stopPropagation: () => void }) => { e.stopPropagation(); onAct({ kind, p }); };
      return <span className="ma-hanh-dong">
        <span className="num">{c.getValue()}</span>
        {canKick ? <Button size="sm" kind="danger--ghost" renderIcon={Logout} onClick={act('kick')}>Kick</Button> : null}
        {canLock ? <Button size="sm" kind="ghost" renderIcon={Locked} onClick={act('games')}>Khóa cược game</Button> : null}
        {canDelete ? <Button size="sm" kind="danger--ghost" renderIcon={TrashCan} onClick={act('xoa')}>Xóa ID</Button> : null}
      </span>;
    } },
    // pinned left while the phone list scrolls sideways, so each row still names its player (client 2026-10-09 read
    // two players of one agent as a repeated row)
    { header: 'Tên đăng nhập', accessorKey: 'user_name', meta: { className: 'cot-dinh' } },
    { header: 'Nick', accessorKey: 'nick_name', cell: (c) => c.getValue() ?? '-' },
    { header: 'Đại lý', id: 'agent', cell: (c) => {
      const a = c.row.original.agent;
      return a ? <span><span className="num">{a.code}</span> · {a.displayName}</span> : (c.row.original.ref_code ? <span className="muted">{c.row.original.ref_code}</span> : '-');
    } },
    { header: `Số dư (${CURRENCY.symbol})`, accessorKey: 'vin', cell: (c) => <Money v={c.getValue()} /> },
    { header: 'Xu', accessorKey: 'xu', cell: (c) => <Money v={c.getValue()} unit="xu" /> },
    { header: 'VIP', accessorKey: 'vip_point', cell: (c) => <span className="num">{c.getValue()}</span> },
    { header: 'Trạng thái', id: 'st', enableSorting: false, cell: (c) => <span className="tags"><PlayerTags p={c.row.original} online={c.row.original.online} /></span> },
    { header: 'Tạo lúc', accessorKey: 'created_at', cell: (c) => <When v={c.getValue()} /> },
  ];
}

const FIELDS: FilterField[] = [
  { key: 'search', label: 'Tìm người chơi', kind: 'text', placeholder: 'Mã, tên đăng nhập hoặc nick', live: true },
  { key: 'locked', label: 'Trạng thái', kind: 'select', options: [['', 'Tất cả'], ['false', 'Đang hoạt động'], ['true', 'Đã khóa']] },
  { key: 'online', label: 'Online', kind: 'select', options: [['', 'Tất cả'], ['true', 'Đang online'], ['false', 'Không online']] },
  { key: 'from', label: 'Tạo từ ngày', kind: 'date' },
  { key: 'to', label: 'Tạo đến ngày', kind: 'date' },
  { key: 'vinMin', label: `Số dư từ (${CURRENCY.symbol})`, kind: 'number' },
  { key: 'vinMax', label: `Số dư đến (${CURRENCY.symbol})`, kind: 'number' },
  { key: 'vipMin', label: 'Điểm VIP từ', kind: 'number' },
  { key: 'vipMax', label: 'Điểm VIP đến', kind: 'number' },
  { key: 'agent', label: 'Mã giới thiệu / đại lý (số)', kind: 'text', placeholder: 'Ví dụ 67896789 hoặc 8888' },
  { key: 'sort', label: 'Sắp xếp', kind: 'select', options: [['created', 'Mới tạo trước'], ['vin', 'Số dư cao trước'], ['vip', 'Điểm VIP cao trước']] },
];
const KEYS = FIELDS.map((f) => f.key);

// header sort = the server's order over every account (P1-D02 sort), the same value the Sắp xếp select and the CSV use
const SORT_COLS: Record<string, string> = { id: 'created', vin: 'vin', vip_point: 'vip' };

const nameOf = (p: Player) => p.user_name || p.nick_name || 'mã ' + p.id;

export function UsersPage() {
  const nav = useNavigate();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [bulk, setBulk] = useState(false);
  const [act, setAct] = useState<RowAct | null>(null);
  const canKick = can('users.kick');
  const canLock = can('users.lock');
  const canDelete = useCanDeleteId();
  const columns = useMemo(() => columnsFor(setAct, canKick, canLock, canDelete), [canKick, canLock, canDelete]);
  const kick = useMutation({
    mutationFn: (id: number) => api.post(`/api/admin/users/${id}/kick-lobby`),
    onSuccess: () => { setAct(null); qc.invalidateQueries({ queryKey: ['users'] }); },
  });
  const closeKick = () => { kick.reset(); setAct(null); };
  const f = useUrlFilters(KEYS, { defaults: { sort: 'created' } });
  const q = useQuery({
    queryKey: ['users', f.query, f.page, f.pageSize],
    queryFn: () => api.get<UsersPage>('/api/admin/users', { ...f.query, page: f.page, pageSize: f.pageSize }),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="page">
      <PageHeader title="Người chơi" lead="Tài khoản trong game. Số dư đọc thẳng từ cơ sở dữ liệu game, chỉ đổi được qua lệnh có người duyệt."
        actions={can('users.lock') ? <Button kind="danger--tertiary" renderIcon={Locked} onClick={() => setBulk(true)}>Khóa / mở khóa hàng loạt</Button> : null} />
      <BulkLockDialog ids={[]} open={bulk} onClose={() => setBulk(false)} />
      <Modal open={act?.kind === 'kick'} danger modalHeading={`Kick ${act ? nameOf(act.p) : ''} về sảnh?`}
        primaryButtonText="Kick" secondaryButtonText="Hủy" primaryButtonDisabled={kick.isPending}
        onRequestClose={closeKick} onRequestSubmit={() => { if (act) kick.mutate(act.p.id); }}>
        <div className="stack">
          <p>Người chơi bị đưa ra khỏi mọi game đang mở và về sảnh, vẫn giữ đăng nhập. Muốn đăng xuất thì dùng Đăng xuất mọi phiên ở trang người chơi; muốn chặn hẳn thì khóa tài khoản.</p>
          <InlineError error={kick.error} />
        </div>
      </Modal>
      {act?.kind === 'games' ? (
        <GameLockDialog key={act.p.id} player={{ id: act.p.id, name: nameOf(act.p) }} onClose={() => setAct(null)} />
      ) : null}
      <XoaIdDialog player={act?.kind === 'xoa' ? { id: act.p.id, name: act.p.user_name } : null} onClose={() => setAct(null)} />
      <FilterBar id="u" fields={FIELDS} filters={f} exportPath="/api/admin/users" />
      <DataTable
        columns={columns}
        data={q.data?.items}
        loading={q.isLoading || q.isPlaceholderData}
        error={q.error}
        onRetry={() => q.refetch()}
        rowKey={(r) => String(r.id)}
        serverSort={{ cols: SORT_COLS, value: f.values.sort || 'created', onChange: (sort) => f.set({ ...f.values, sort }) }}
        onRowClick={(r) => nav('/users/' + r.id)}
        empty={{ title: f.active ? 'Không tìm thấy người chơi' : 'Chưa có người chơi', body: f.active ? 'Không có tài khoản khớp bộ lọc. Nới bộ lọc để xem thêm.' : undefined }}
      />
      {q.data && q.data.total > 0 ? (
        <Pagination page={f.page} pageSize={f.pageSize} pageSizes={[25, 50, 100, 200]} totalItems={q.data.total}
          itemsPerPageText="Mỗi trang" pageRangeText={(_c, t) => `trên ${t} trang`}
          itemRangeText={(min, max, total) => `${min} đến ${max} trong ${total.toLocaleString('vi-VN')} người chơi`}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
    </div>
  );
}
